'use client'

import { EmptyState } from '@/components/ui/empty-state'
import { ReactNode } from 'react'
import { FilterBar } from './filter-bar'
import { FilterDrawer } from './filter-drawer'
import { SortOption, useContentFilter } from './use-content-filter'

interface Category {
  name: string
  slug: string
}

export interface ContentFilterProps<T> {
  items: T[]
  categories: Category[]
  sortOptions: SortOption<T>[]
  searchFields: (keyof T)[]
  categoryAccessor: (item: T) => string | null | undefined
  renderItems: (filtered: T[]) => ReactNode
  searchPlaceholder?: string
  defaultSort?: string
  emptyState?: ReactNode
}

export function ContentFilter<T>({
  items,
  categories,
  sortOptions,
  searchFields,
  categoryAccessor,
  renderItems,
  searchPlaceholder = 'Search & filter...',
  defaultSort = 'newest',
  emptyState,
}: ContentFilterProps<T>) {
  const {
    search,
    sort,
    category,
    isExpanded,
    hasActiveFilters,
    activeSortLabel,
    filtered,
    setSearch,
    setSort,
    setCategory,
    setIsExpanded,
    clearFilters,
  } = useContentFilter({
    items,
    searchFields,
    sortOptions,
    defaultSort,
    categoryAccessor,
  })

  // Find the display name for the active category
  const activeCategoryName =
    category !== 'all'
      ? categories.find((c) => c.slug.toLowerCase() === category.toLowerCase())?.name || category
      : null

  const sortOptionDisplays = sortOptions.map((o) => ({
    label: o.label,
    value: o.value,
  }))

  return (
    <div>
      {/* Filter bar + drawer — single bordered container */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="border border-border rounded-xl bg-white transition-colors hover:border-accent/30">
          <FilterBar
            isExpanded={isExpanded}
            onToggle={() => setIsExpanded(!isExpanded)}
            activeCategory={activeCategoryName}
            activeSortLabel={sort !== defaultSort ? activeSortLabel : null}
            hasActiveFilters={hasActiveFilters}
            placeholder={searchPlaceholder}
            search={search}
            onSearchChange={setSearch}
          />
          <FilterDrawer
            isOpen={isExpanded}
            sort={sort}
            sortOptions={sortOptionDisplays}
            onSortChange={setSort}
            category={category}
            categories={categories}
            onCategoryChange={setCategory}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
          />
        </div>
      </div>

      {/* Filtered content */}
      {filtered.length === 0 ? (
        emptyState || (
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
            <EmptyState
              icon="search"
              title="No results found"
              description="Try adjusting your search or filters."
              primary={{ label: 'Clear filters', onPress: clearFilters }}
            />
          </div>
        )
      ) : (
        renderItems(filtered)
      )}
    </div>
  )
}
